import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { isEqual } from 'lodash-es'
import { useApi } from 'ply-api/Provider'
import * as React from 'react'
import { Deferred } from '../Deferred'
import {
	appendToCache,
	getMissingIds,
	useEntityCache,
} from '../entity-cache-provider'
import omit from '../omit'

const emptyArr = []

export function useEntityQueryByIDs<TEntity extends { _kind: string }>(
	name: TEntity['_kind'],
	ids: string[],
	options?:
		| (Omit<UseQueryOptions<TEntity[], unknown, TEntity[]>, 'queryKey'> & {
				rootProperty?: string | false
				valueProperty?: string | number | symbol
				onSuccess?: (data: TEntity[]) => void
				disableBaseUrl?: boolean
		  })
		| undefined,
): TEntity[] {
	const cache = useEntityCache(name, options?.valueProperty)
	const { baseURL } = useApi()

	const initialData = React.useMemo<TEntity[] | undefined>(() => {
		const cachedIds = ids
			.filter((id) => cache[id])
			.map((id) => cache[id]) as TEntity[]

		if (cachedIds?.length) {
			return cachedIds
		}

		return undefined
	}, [cache, ids])

	const missingIds = getMissingIds(name, ids, options?.valueProperty)
	const hasMissingIds = Boolean(missingIds.length)

	useQuery<TEntity[]>({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: [name, missingIds],
		queryFn: () => {
			if (!missingIds?.length) {
				return emptyArr as TEntity[]
			}
			const base = options?.disableBaseUrl
				? ''
				: `${baseURL.replace(/\/$/, '')}/`
			return fetch(
				`${base}${name}?${missingIds
					.map((id) => `id=${encodeURIComponent(id)}`)
					.join('&')}`,
			)
				.then((res) => res.json())
				.then((res) =>
					options?.rootProperty !== false
						? res?.[options?.rootProperty ?? 'rows']
						: res,
				)
				.then((data) => {
					options?.onSuccess?.(data)

					appendToCache(name, options?.valueProperty)(data as any)

					return data
				})
		},
		...options,
		initialData,
		refetchOnWindowFocus: false,
		enabled: (options?.enabled ?? true) && hasMissingIds,
	})

	const lastResult = React.useRef<any>(undefined)

	const result = React.useMemo(() => {
		const cachedIds = ids.map((id) => cache?.[id] ?? { id }) as TEntity[]

		if (lastResult.current && isEqual(lastResult.current, cachedIds)) {
			return lastResult.current
		}

		lastResult.current = cachedIds.length ? cachedIds : emptyArr

		return lastResult.current
	}, [cache, ids])

	return result
}

export function useLazyEntityQueryByIDs<TEntity extends { _kind: string }>(
	name: TEntity['_kind'],
	options?:
		| (Omit<UseQueryOptions<TEntity[], unknown, TEntity[]>, 'queryKey'> & {
				rootProperty?: string | false
				valueProperty?: string | number | symbol
				onSuccess?: (data: TEntity[]) => void
				disableBaseUrl?: boolean
		  })
		| undefined,
) {
	const deferredRef = React.useRef<Deferred<TEntity[]> | null>(null)
	const [ids, setIds] = React.useState<string[]>([])
	const cache = useEntityCache(name, options?.valueProperty)
	const { baseURL } = useApi()

	const initialData = React.useMemo<TEntity[]>(() => {
		const cachedIds = ids
			.filter((id) => cache?.[id])
			.map((id) => cache?.[id]) as TEntity[]

		if (cachedIds?.length) {
			return cachedIds
		}

		return []
	}, [cache, ids])

	useQuery<TEntity[]>({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: [name, ids],
		queryFn: () => {
			if (!ids?.length) {
				return emptyArr as TEntity[]
			}

			const missingIds = getMissingIds(name, ids, options?.valueProperty)

			if (!missingIds?.length) {
				return emptyArr as TEntity[]
			}

			const base = options?.disableBaseUrl
				? ''
				: `${baseURL.replace(/\/$/, '')}/`
			return fetch(
				`${base}${name}?${ids
					.map((id) => `id=${encodeURIComponent(id)}`)
					.join('&')}`,
			)
				.then((res) => res.json())
				.then((res) =>
					options?.rootProperty === false
						? res
						: res?.[options?.rootProperty ?? 'rows'],
				)
				.then((data) => {
					options?.onSuccess?.(data)

					const cache = appendToCache(name, options?.valueProperty)(data as any)

					const cachedIds = ids.map((id) => cache?.[id] ?? null) as TEntity[]

					deferredRef.current?.resolve(cachedIds)

					return data
				})
		},
		...(options ? omit(options, 'onSuccess') : {}),
		initialData,
		refetchOnWindowFocus: false,
		enabled:
			options?.enabled &&
			Boolean(ids?.length && (initialData?.length ?? 0) < ids.length),
	})

	return {
		fetch: (
			ids: string[],
			manageExact?: boolean,
			pasteFilterProperty?: string,
		) => {
			if (pasteFilterProperty) {
				const values = ids

				return fetch(
					`${baseURL.replace(/\/$/, '')}/${name}?${ids
						.map((id) => `${pasteFilterProperty}=${encodeURIComponent(id)}`)
						.join('&')}`,
				)
					.then((res) => res.json())
					.then((res: any) =>
						options?.rootProperty === false
							? res
							: res[options?.rootProperty ?? 'rows'],
					)
					.then((data) => {
						const matchs = values
							.map((v) => data.find((r) => r[pasteFilterProperty] === v))
							.filter(Boolean)

						options?.onSuccess?.(matchs)

						const cache = appendToCache(
							name,
							options?.valueProperty,
						)(data as any)

						const cachedIds = values.map(
							(id) => cache?.[id] ?? null,
						) as TEntity[]

						deferredRef.current?.resolve(cachedIds)
						return matchs
					})
			}

			if (manageExact) {
				return fetch(
					`${baseURL.replace(/\/$/, '')}/${name}?${ids
						.map((id) => `exact=${encodeURIComponent(id)}`)
						.join('&')}`,
				)
					.then((res) => res.json())
					.then((res: any) =>
						options?.rootProperty === false
							? res
							: res[options?.rootProperty ?? 'rows'],
					)
					.then((data) => {
						options?.onSuccess?.(data)

						const cache = appendToCache(
							name,
							options?.valueProperty,
						)(data as any)

						const cachedIds = ids.map((id) => cache?.[id] ?? null) as TEntity[]

						deferredRef.current?.resolve(cachedIds)

						return data
					})
			}

			setIds(ids)

			const cachedIds = ids
				.map((id) => cache[id] ?? null)
				.filter(Boolean) as TEntity[]

			if (cachedIds?.length === ids?.length) {
				return Promise.resolve(cachedIds)
			}

			const deferred = new Deferred<TEntity[]>()

			deferredRef.current = deferred

			return deferred
		},
	}
}

export { appendToCache }
